import { Point, ShapeLineConfig, TDynamicVariable } from "@shared/types";

import { TMinimumShape, CoreConfig, InternalCoreConfig } from "./types";

export const roundNumber = (number: number, config: InternalCoreConfig) => {
  return parseFloat(number.toFixed(config.roundTo || 10)) || 0;
};

export const calculateDistance = (
  p1: { x: number; y: number },
  p2: { x: number; y: number }
) => {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
};

export const getLength = (shape: TMinimumShape , debug = false) => {
  const config: ShapeLineConfig = shape.config as any;
  if (shape.measuredLength) {
    return shape.measuredLength;
  }

  // Calculate arc length for 3_POINT_CURVE
  if ((shape.type as string) === "3_POINT_CURVE") {
    const threePointConfig = config as any;
    if (threePointConfig.start && threePointConfig.end && threePointConfig.middle) {
      const source: any = shape.PlanPage?.source;
      const scale = source?.scale || 1;
      
      // Get points
      const p1 = threePointConfig.start;
      const p2 = threePointConfig.middle;
      const p3 = threePointConfig.end;
      
      // Step 1: Find center of circle passing through all points
      const midPoint1 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const midPoint2 = { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 };
      
      const slope1 = (p2.y - p1.y) / (p2.x - p1.x);
      const slope2 = (p3.y - p2.y) / (p3.x - p2.x);
      
      // Perpendicular slopes (handle special cases for vertical/horizontal lines)
      const bisectorSlope1 = slope1 !== 0 ? -1 / slope1 : 1000000;
      const bisectorSlope2 = slope2 !== 0 ? -1 / slope2 : 1000000;
      
      // Center calculation (handle special cases)
      let centerX, centerY;
      
      if (Math.abs(bisectorSlope1) > 999999) {
        // First line is vertical
        centerX = midPoint1.x;
        centerY = bisectorSlope2 * (centerX - midPoint2.x) + midPoint2.y;
      } else if (Math.abs(bisectorSlope2) > 999999) {
        // Second line is vertical
        centerX = midPoint2.x;
        centerY = bisectorSlope1 * (centerX - midPoint1.x) + midPoint1.y;
      } else {
        // Find intersection of perpendicular bisectors
        centerX = (midPoint2.y - midPoint1.y + bisectorSlope1 * midPoint1.x - bisectorSlope2 * midPoint2.x) / 
                  (bisectorSlope1 - bisectorSlope2);
        centerY = bisectorSlope1 * (centerX - midPoint1.x) + midPoint1.y;
      }
      
      // Step 2: Calculate radius
      const radius = Math.sqrt(Math.pow(p1.x - centerX, 2) + Math.pow(p1.y - centerY, 2));
      
      // Step 3: Calculate angles
      const angle1 = Math.atan2(p1.y - centerY, p1.x - centerX);
      const angle2 = Math.atan2(p2.y - centerY, p2.x - centerX);
      const angle3 = Math.atan2(p3.y - centerY, p3.x - centerX);
      
      // We need to find the arc that goes from p1 to p3 through p2
      // For fence calculations, we want the arc that actually passes through the middle point
      
      // We need to find the arc that goes from p1 to p3 through p2
      // Check if angle2 is between angle1 and angle3 going counterclockwise
      const isAngleBetween = (start: number, mid: number, end: number): boolean => {
        // Normalize angles to [0, 2π)
        const normalize = (a: number) => {
          let result = a;
          while (result < 0) result += 2 * Math.PI;
          while (result >= 2 * Math.PI) result -= 2 * Math.PI;
          return result;
        };
        
        const s = normalize(start);
        const m = normalize(mid);
        const e = normalize(end);
        
        if (s <= e) {
          // No wrap around
          return s <= m && m <= e;
        } else {
          // Wrap around 0
          return m >= s || m <= e;
        }
      };
      
      let angleDiff: number;
      
      // Check counterclockwise direction (positive angle difference)
      if (isAngleBetween(angle1, angle2, angle3)) {
        // Go counterclockwise from angle1 to angle3
        angleDiff = angle3 - angle1;
        if (angleDiff < 0) angleDiff += 2 * Math.PI;
      } else {
        // Go clockwise from angle1 to angle3
        angleDiff = angle1 - angle3;
        if (angleDiff < 0) angleDiff += 2 * Math.PI;
      }
      
      // Step 5: Calculate arc length with scale factor
      const arcLength = radius * angleDiff * scale;
      
      if (debug) {
        console.log("3-point curve debug:", {
          p1, p2, p3,
          center: { x: centerX, y: centerY },
          radius,
          angle1: angle1 * 180 / Math.PI + "°",
          angle2: angle2 * 180 / Math.PI + "°", 
          angle3: angle3 * 180 / Math.PI + "°",
          angleDiff: angleDiff * 180 / Math.PI + "°",
          angleDiffRadians: angleDiff,
          scale,
          arcLength: arcLength
        });
      }
      
      return arcLength;
    }
  }
  
 if (config.start && config.end) {
    if( debug ){
      // @ts-ignore
      console.log( "Scale" , shape.PlanPage?.source?.scale );
    }

    const source: any = shape.PlanPage?.source;
    return calculateDistance(config.start, config.end) * (source?.scale || 1);
} 
  if (shape.computedLength) {
    if( debug ) console.log("CL", shape.measuredLength );
    return shape.computedLength;
  }
  return 0;
};
type TMergeDataShape = Omit<TMinimumShape, "config"> & {
  config: TMinimumShape["config"] & {
    linkedLines?: string[];
  };
};

const getPointType = (config: CoreConfig, id: string, debug?: boolean) => {
  return config.Shapes.find((s) => s._id === id)?.config?.posttype;
};

const getLinkedLines = (config: CoreConfig, id: string, exclude?: string[]) => {
  return config.Shapes.filter(
    (s) => {
      // Check regular LINE shapes
      if (s.config?.end?.link === id || s.config?.start?.link === id) {
        return true;
      }
      // Check 3_POINT_CURVE shapes for middle point link
      if ((s.type as string) === "3_POINT_CURVE" && (s.config as any)?.middle?.link === id) {
        return true;
      }
      return false;
    }
  ).filter((l) => {
    if (!exclude) return true;
    return !exclude.includes(l._id || "noid");
  });
};


const isLinkedNotContinued = ( point : Point  , config: CoreConfig) => {
  const { link } = point;
  if( !link ) return false;
  const shape = config?.Shapes?.find( s => s?._id === link );
  return shape?.config?.posttype !== "LINE_POST"
}

const mergeLines = (lines: TMergeDataShape[], config: CoreConfig) => {
  
    const lineConfig: { start?: Point, end?: Point, middle?: Point}  = lines.reduce( ( pv, cv ) => {
      const val = { ...pv };
      const useStart = isLinkedNotContinued( cv.config?.start || {} , config );
      const useEnd = isLinkedNotContinued( cv.config?.end || {} , config );
      if( useStart ){
        val.start = cv.config?.start;
      }
      if( useEnd ){
        val.end = cv.config?.end;
      }
      // For 3-point curves, also check middle point
      if ((cv.type as string) === "3_POINT_CURVE") {
        const useMiddle = isLinkedNotContinued( (cv.config as any)?.middle || {} , config );
        if( useMiddle ){
          val.middle = (cv.config as any)?.middle;
        }
      }
      return val;

    } , {} as { start?: Point, end?: Point, middle?: Point} );
  
  const base = { ...lines[0] };
  base.measuredLength = lines
    .map((l) => {
      return getLength(l);
    })
    .reduce((a, b) => a + b, 0);
  base.computedLength = lines
    .map((l) => l.computedLength || 0)
    .reduce((a, b) => a + b, 0);
    base.config = { ...base.config, ...lineConfig}
  return base;
};

const getMergedLines = (lines: TMergeDataShape[], config: CoreConfig, debug = false) => {
  const linkedLines = lines.filter(
    (l) => (l.config?.linkedLines || []).length !== 0
  );
  
  // Build a map of all connections
  const connections: Record<string, Set<string>> = {};
  for (const line of linkedLines) {
    const id = line._id || "";
    if (!connections[id]) {
      connections[id] = new Set<string>();
    }
    // Add bidirectional connections for each linked line
    for (const linkedId of (line.config?.linkedLines || [])) {
      connections[id].add(linkedId);
      if (!connections[linkedId]) {
        connections[linkedId] = new Set<string>();
      }
      connections[linkedId].add(id);
    }
  }

  // Find all connected components using DFS
  const visited = new Set<string>();
  const segments: string[][] = [];
  
  const dfs = (id: string, currentSegment: string[]) => {
    if (visited.has(id)) return;
    visited.add(id);
    currentSegment.push(id);
    
    // Get all lines connected through LINE_POST points
    const line = lines.find(l => l._id === id);
    if (line) {
      // Get link IDs based on shape type
      const linkIds: (string | undefined)[] = [];
      if ((line.type as string) === "3_POINT_CURVE") {
        linkIds.push(line.config.start?.link, (line.config as any)?.middle?.link, line.config.end?.link);
      } else {
        linkIds.push(line.config.start?.link, line.config.end?.link);
      }
      
      for (const linkId of linkIds) {
        if (linkId) {
          const pointType = getPointType(config, linkId, debug);
          if (pointType === "LINE_POST") {
            const connectedLines = getLinkedLines(config, linkId, [id]);
            for (const connectedLine of connectedLines) {
              const connectedId = connectedLine._id || "";
              if (!visited.has(connectedId)) {
                dfs(connectedId, currentSegment);
              }
            }
          }
        }
      }
    }
  };

  // Start DFS from each unvisited line
  for (const line of linkedLines) {
    const id = line._id || "";
    if (!visited.has(id)) {
      const currentSegment: string[] = [];
      dfs(id, currentSegment);
      segments.push(currentSegment);
    }
  }

  if (debug) {
    console.log("Connected Segments:", segments);
  }

  // Merge each segment
  return segments.map((segmentIds) => {
    return mergeLines(
      segmentIds.map(
        (id) => lines.find((l) => l._id === id) as TMergeDataShape
      ),
      config
    );
  });
};

export const getComputedLines = (
  config: CoreConfig,
  subtype?: TDynamicVariable["subtype"],
  debugprop = false
) => {
  const debug = config.Shapes.map((s) => s._id || "").includes(
    "c765943f-2515-4a2e-bdf7-76595f1ab2b4"
  );
  
  if (debugprop) {
    console.log("Points:", config.Shapes.filter(s => s.type === "POINT").map(p => ({
      id: p._id,
      type: p.config?.posttype,
      x: p.config?.point?.x,
      y: p.config?.point?.y
    })));
  }

  const lines: TMergeDataShape[] = config.Shapes.filter(
    (l) => l.type === "LINE" || !l.type || (l.type as string) === "3_POINT_CURVE"
  )
    .filter((l) => {
      if (!subtype) {
        return true;
      }
      return subtype.includes(l.config.fencetype || "FENCE");
    })
    .map((l) => {
      const linkedLines: string[] = [];
      // Handle both LINE and 3_POINT_CURVE shapes
      const linkIds: (string | undefined)[] = [];
      if ((l.type as string) === "3_POINT_CURVE") {
        linkIds.push(l.config.start?.link, (l.config as any)?.middle?.link, l.config.end?.link);
      } else {
        linkIds.push(l.config.start?.link, l.config.end?.link);
      }
      
      for (const linkId of linkIds) {
        if (linkId) {
          const startType = getPointType(config, linkId, debug);
          if (startType === "LINE_POST") {
            const ll = getLinkedLines(config, linkId, [l._id || ""]);
            if (ll.length > 0) {
              const id = ll[0]._id || "";
              if (!linkedLines.includes(id)) {
                linkedLines.push(id);
              }
            }
          }
        }
      }
      if (debugprop) {
        console.log("Line:", {
          id: l._id,
          type: l.type,
          start: l.config.start,
          end: l.config.end,
          middle: (l.config as any)?.middle,
          linkedLines
        });
      }
      return {
        ...l,
        config: {
          ...l.config,
          linkedLines,
        },
      };
    });
  const unlinkedLines = lines.filter(
    (l) => (l.config?.linkedLines || []).length === 0
  );
  if( debugprop ){
     console.log( "ML" , getMergedLines(lines, config) );
    // return lines;
  } 
  
  return [...unlinkedLines, ...getMergedLines(lines, config)];
};

export const usesParentValue = (categoryId: string, config: CoreConfig) => {
  const category = config?.ProductConfiguration?.Categories?.find((c) => {
    return c._id === categoryId;
  });
  if (!category) return false;
  if (!config.parent) return false;
  return category?.useParentValues || false;
};
